import {
    inject,
    Injectable,
    Injector,
    signal,
    Signal,
    ViewContainerRef,
    WritableSignal
} from "@angular/core";

import { WidgetComponent } from "@main/reporting.v3/widget/widget.component";
import { WidgetInputAlias } from "@main/reporting.v3/widget/models/widget-input-alias.enum";

import { WIDGET_HUB, WIDGET_RESOLVER } from "../constants/widget-hub.constants";
import {
    WidgetHub,
    WidgetInfo,
    WidgetInstance,
    WidgetRegister,
    WidgetResolver,
    WidgetReference
} from "../models";


@Injectable()
export class WidgetHubService implements WidgetHub {

    private parent: WidgetHub = inject(WIDGET_HUB, { optional: true, skipSelf: true });
    private resolver: WidgetResolver = inject(WIDGET_RESOLVER);
    private view: ViewContainerRef = inject(ViewContainerRef);
    private injector: Injector = inject(Injector);

    // Service writable signals.
    private register: WritableSignal<WidgetRegister> = signal({});

    // Service fields.
    private infos: Record<string, WidgetInfo> = {};

    // TODO: Temporary approach until we implement a widget store.
    public widgetSourceId: string;


    // Service methods.
    public resetWidgetUsages(): void {
        const instances: WidgetInstance[] = this.getInstances();

        instances.forEach(instance => instance.taken.set(false));
    }

    public removeUnusedWidgets(): void {
        const register: WidgetRegister = this.register();

        if (!register) return;

        const sets: WritableSignal<WidgetInstance[]>[] = Object.values(register);

        sets.forEach(set => {

            // Destroy all unused widget components.
            set().filter(instance => !instance.taken())
                .forEach(instance => instance.component.destroy());

            // Filter each set from unused widget instances.
            set.update(instances => instances.filter(instance => instance.taken()));
        });
    }

    public getWidgets(): WidgetReference[] {
        const instances: WidgetInstance[] = this.getInstances();

        const taken: WidgetInstance[] = instances.filter(instance => instance.taken());

        return taken.map(instance => instance.component);
    }

    public canRender(widgetSourceId: string): boolean {
        // A hub cannot render an instance of parent component widget.
        if (this.widgetSourceId === widgetSourceId) return false;

        // A hub cannot render an instance of ancestor component widget.
        return !this.parent || this.parent.canRender(widgetSourceId);
    }

    public requestWidget(info: WidgetInfo): WidgetReference {
        if (!info?.id || !info?.widgetType) return null;

        const register: WidgetRegister = this.register();

        if (!register) return null;

        // If there is a rendering loop, return null to avoid it.
        if (!this.canRender(info.id)) return null;

        if (!register[info.id]) {
            // If this widget source id is missing, register it.
            // Update the register to trigger any dependent signal.
            this.register.update(register => {

                register[info.id] = signal([]);

                return { ...register };
            });
        }

        const set: WritableSignal<WidgetInstance[]> = register[info.id];

        const available: WidgetInstance = set().find(instance => !instance.taken());

        const instance: WidgetInstance = available ?? this.createWidget(info);

        if (!available) {
            // If a new widget instance was necessary, register it.
            // Update the set to trigger any dependent signal.
            set.update(instances => {

                instances.push(instance);

                return [...instances];
            });
        }

        // Mark the instance as taken.
        instance.taken.set(true);

        return instance.component;
    }

    public async requestWidgetByCode(widgetCode: string): Promise<WidgetReference> {
        const info: WidgetInfo = await this.requestWidgetInfo(widgetCode);

        if (!info?.id || !info?.widgetType) return null;

        return this.requestWidget(info)
    }


    private getInstances(): WidgetInstance[] {
        const register: WidgetRegister = this.register();

        if (!register) return [];

        const sets: Signal<WidgetInstance[]>[] = Object.values(register);

        return sets.flatMap(set => set());
    }

    private createWidget(info: WidgetInfo): WidgetInstance {
        // Create the widget component.
        const widget: WidgetReference = this.view.createComponent(WidgetComponent, { injector: this.injector });

        // Set the corresponding widget source id and type.
        widget.setInput(WidgetInputAlias.Exportable, false);
        widget.setInput(WidgetInputAlias.WidgetSourceId, info.id);
        widget.setInput(WidgetInputAlias.WidgetType, info.widgetType);

        // Create the widget instance and register in the list as non-taken.
        const instance: WidgetInstance = { component: widget, taken: signal(false) };

        return instance;
    }

    private async requestWidgetInfo(widgetCode: string): Promise<WidgetInfo> {
        if (!widgetCode?.length) return null;

        this.infos[widgetCode] ??= await this.resolver.getWidgetInfo(widgetCode);

        return this.infos[widgetCode];
    }
}
